import React, {useMemo, useState} from "react";
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Collapse,
    Grid,
    IconButton,
    Link,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from "@mui/material";
import {yellow} from "@mui/material/colors";
import {DeskRef, EmployeeRef, INeighbourhood} from "../../api/Model";
import EmployeeList from "../employee-list/EmployeeList";
import EmployeeAvatar from "../employee-avatar/EmployeeAvatar";
import DeskChip from "../desk-chip/DeskChip";
import NeighbourhoodColorIcon from "../neighbourhood-icon/NeighbourhoodColorIcon";
import {VisitChart} from "../visit-chart/VisitChart";
import {
    FigureVisitStatBlock,
    OverflowDaysStatBlock,
    VisitStatBlock,
    WeekVisitStatBlock
} from "../visit-stat-block/VisitStatBlock";
import EditIcon from "@mui/icons-material/Edit";
import {StatFigure, StatRanges, VisitWithAssignedDesksStats} from "../visit-stat-block/OfficeVisitStats";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {AssignedDesks} from "../../api/AssignedDeskStore";
import SlackIcon from "../icons/SlackIcon";
import {useNeighbourhoodSlackUrl} from "./useNeighbourhoodSlackUrl";
import EmployeeLink from "../employee-link/EmployeeLink";
import {HorizontalScroller} from "../horizontal-scroller/HorizontalScroller";
import {PRINT_AVOID_BREAK} from "../../util/PrintUtil";

function NeighbourhoodEmployeeStat({stats, assignedDesks}: {
    stats: VisitWithAssignedDesksStats,
    assignedDesks: AssignedDesks,
}) {
    return (
        <VisitStatBlock
            tooltip={<EmployeeList stats={stats} assignedDesks={assignedDesks}/>}>
            Members: {stats.employeeStats.employees.length}
        </VisitStatBlock>
    );
}

type EmployeeDeskRow = {
    key: string,
    employeeData?: {
        employee: EmployeeRef,
        visits: StatFigure,
        shouldHaveDesk: boolean,
        deskComment: string,
    },
    desk?: DeskRef,
    outsideNeighbourhood?: boolean,
};

export function NeighbourhoodPaneEmployeeBlock({stats, desks, assignedDesks}: {
    stats: VisitWithAssignedDesksStats,
    assignedDesks: AssignedDesks,
    desks: DeskRef[],
}) {
    const [expanded, setExpanded] = useState(false);
    const hasEmployeesOrDesks = stats.employeeStats.employees.length > 0 || desks.length > 0;
    const rows = useMemo(() => {
        const neighbourhoodDeskIds = new Set(desks.map(desk => desk.id));
        return [
            ...stats.employeeStats.employees.flatMap<EmployeeDeskRow>(({employee, visits}) => {
                const employeeDesks = assignedDesks.desksOf(employee);
                const shouldHaveDesk = stats.employeeStats.assignedDeskRecommendationEmployees.has(employee.id);
                const employeeData = {
                    employee,
                    visits,
                    shouldHaveDesk: shouldHaveDesk,
                    deskComment: stats.assignedDeskCriteria.deskComment(shouldHaveDesk),
                };
                return employeeDesks.length > 0 ? employeeDesks.map(desk => ({
                    key: `employee-${employee.id}-desk-${desk.id}`,
                    employeeData,
                    desk: desk,
                    outsideNeighbourhood: neighbourhoodDeskIds ? !neighbourhoodDeskIds.has(desk.id) : false,
                })) : [{
                    key: `employee-${employee.id}`,
                    employeeData,
                    desk: undefined,
                }];
            }),
            ...desks
                .filter(desk => !assignedDesks.assigneeOf(desk))
                .map<EmployeeDeskRow>(desk => ({
                    key: `desk-${desk.id}`,
                    employee: undefined,
                    visits: undefined,
                    desk,
                })),
        ];
    }, [stats, assignedDesks, desks]);

    return (
        <Grid>
            {hasEmployeesOrDesks ? (
                <Stack gap={1}>
                    <Stack direction="row" gap={1} alignItems="center">
                        <Tooltip title={expanded ? "Collapse employee details" : "Expand employee details"}>
                            <IconButton
                                aria-label={expanded ? "Collapse employee details" : "Expand employee details"}
                                size="small"
                                onClick={() => setExpanded(value => !value)}
                            >
                                {expanded ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
                            </IconButton>
                        </Tooltip>
                        <HorizontalScroller sx={{flex: 1}}>
                            {rows.map(row => row.desk ? (
                                <DeskChip
                                    key={row.key}
                                    desk={row.desk}
                                    deskAssignee={row.employeeData?.employee}
                                    outsideNeighbourhood={row.outsideNeighbourhood}
                                />
                            ) : row.employeeData ? (
                                <EmployeeAvatar
                                    key={row.key}
                                    employee={row.employeeData.employee}
                                    showNameTooltip
                                    sx={{width: 30, height: 30}}
                                />
                            ) : null)}
                        </HorizontalScroller>
                    </Stack>
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Employee</TableCell>
                                    <TableCell>Desk</TableCell>
                                    <TableCell align="right">Visits</TableCell>
                                    <TableCell>Desk recommendation</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map(row => {
                                    return (
                                        <TableRow key={row.key} sx={{height: 44}}>
                                            <TableCell>
                                                {row.employeeData ? (
                                                    <Stack direction="row" alignItems="center" gap={1}>
                                                        <EmployeeAvatar employee={row.employeeData.employee}
                                                                        sx={{width: 30, height: 30}}/>
                                                        <EmployeeLink employee={row.employeeData.employee}/>
                                                    </Stack>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Shared desk
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {row.desk ? (
                                                    <Stack direction="row" gap={1} flexWrap="wrap">
                                                        <DeskChip
                                                            desk={row.desk}
                                                            outsideNeighbourhood={row.outsideNeighbourhood}
                                                            hideAssignee
                                                            deskAssignee={row.employeeData?.employee}
                                                            size="small"
                                                        />
                                                    </Stack>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">No assigned
                                                        desk</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {row.employeeData ? `${row.employeeData.visits.value} (${row.employeeData.visits.percent}%)` : ""}
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    color={row.employeeData?.shouldHaveDesk ? "text.primary" : "text.secondary"}
                                                >
                                                    {row.employeeData?.deskComment}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Collapse>
                </Stack>
            ) : (
                <Typography color="text.secondary">No employees</Typography>
            )}
        </Grid>
    );
}

export function NeighbourhoodPaneStatBlock({deskCount, stats, assignedDesks, statRanges}: {
    deskCount: number,
    stats: VisitWithAssignedDesksStats,
    assignedDesks: AssignedDesks,
    statRanges: StatRanges,
}) {
    return (
        <Stack direction="row" alignItems="baseline" gap={2} flexWrap="wrap">
            <VisitStatBlock
                tooltip={
                    <Typography variant="body2">
                        Number desks assigned to this neighbourhood: {deskCount}
                    </Typography>
                }
            >
                Desks: {deskCount}
            </VisitStatBlock>
            <NeighbourhoodEmployeeStat stats={stats} assignedDesks={assignedDesks}/>
            <VisitStatBlock
                tooltip={
                    <Typography variant="body2">
                        {stats.assignedDeskCriteria.description}: {stats.employeeStats.assignedDeskRecommendationEmployees.size}
                    </Typography>
                }
            >
                Assign: {stats.employeeStats.assignedDeskRecommendationEmployees.size}
            </VisitStatBlock>
            <FigureVisitStatBlock stats={stats} statRanges={statRanges} field="avgVisits"/>
            <FigureVisitStatBlock stats={stats} statRanges={statRanges} field="median"/>
            <FigureVisitStatBlock stats={stats} statRanges={statRanges} field="percentile90"/>
            <FigureVisitStatBlock stats={stats} statRanges={statRanges} field="maxVisits"/>
            <WeekVisitStatBlock visits={stats.visits}/>
            <OverflowDaysStatBlock stats={stats} statRanges={statRanges}/>
        </Stack>
    );
}

export function NeighbourhoodPaneVisitChartBlock({stats, deskCount}: {
    stats: VisitWithAssignedDesksStats,
    deskCount: number,
}) {
    return (
        <Box width="100%" height="400px">
            <VisitChart
                visits={stats.visits}
                deskCount={deskCount}
                recommendedAssignedDeskCount={stats.employeeStats.assignedDeskRecommendationEmployees.size}
            />
        </Box>
    );
}

export function NeighbourhoodCard<Hood extends INeighbourhood>(
    {
        neighbourhood,
        highlight = false,
        onEdit,
        onDelete,
        children,
    }: {
        neighbourhood: Hood,
        highlight?: boolean,
        onEdit?: (neighbourhood: Hood) => void,
        onDelete?: (neighbourhood: Hood) => void,
        children?: React.ReactNode,
    }) {

    const slackUrl = useNeighbourhoodSlackUrl(neighbourhood);

    const handleEdit = useMemo(() => {
        return onEdit && (() => {
            onEdit(neighbourhood);
        })
    }, [neighbourhood, onEdit]);

    const handleDelete = useMemo(() => {
        return onDelete && (() => {
            onDelete(neighbourhood);
        })
    }, [neighbourhood, onDelete]);

    return (
        <Card
            className={PRINT_AVOID_BREAK}
            sx={{backgroundColor: highlight ? yellow[50] : undefined}}
        >
            <CardHeader
                avatar={
                    <NeighbourhoodColorIcon
                        neighbourhood={neighbourhood}
                        size="2.5rem"
                    />
                }
                slotProps={{title: {variant: neighbourhood.notes ? undefined : 'h5'}}}
                title={neighbourhood.name}
                subheader={neighbourhood.notes}
                action={
                    <Stack direction="row">
                        {slackUrl && (
                            <Tooltip title="Open neighbourhood Slack channel">
                                <IconButton color="primary" component={Link} href={slackUrl} target="_blank">
                                    <SlackIcon colored/>
                                </IconButton>
                            </Tooltip>
                        )}
                        {handleEdit && (
                            <Tooltip title="Edit">
                                <IconButton color="primary" onClick={handleEdit}>
                                    <EditIcon/>
                                </IconButton>
                            </Tooltip>
                        )}
                        {handleDelete && (
                            <Tooltip title="Delete">
                                <IconButton color="error" onClick={handleDelete}>
                                    <DeleteIcon/>
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                }
            />
            <CardContent>
                <Stack gap={2}>
                    {children}
                </Stack>
            </CardContent>
        </Card>
    );
}
